import { formatDate } from '@/dates'
import type {
	FullRejectionDetail,
	FullRejectionPhoto
} from '@/features/host-review/types'
import { getThumbnailUrl } from '@/features/host-review/utils'
import { useTranslation } from '@/translations/i18n'

const DetailField = ({
	label,
	value
}: {
	label: string
	value: string | null
}) => {
	const { t } = useTranslation()

	return (
		<div className='flex flex-col gap-0.5'>
			<span className='text-neutral-600 text-xs/4'>{label}</span>
			<span className='text-neutral-900 text-sm/5'>
				{value ?? t('host_review:full_rejections.unknown')}
			</span>
		</div>
	)
}

/**
 * Groups the set the way the host saw it. Photos with no room allocation are
 * kept together at the end rather than dropped - the host reviewed them too.
 */
const groupByRoom = (photos: FullRejectionPhoto[]) => {
	const groups = new Map<string | null, FullRejectionPhoto[]>()

	for (const photo of photos) {
		const existing = groups.get(photo.roomKey)
		if (existing) {
			existing.push(photo)
		} else {
			groups.set(photo.roomKey, [photo])
		}
	}

	return [...groups.entries()].sort(([a], [b]) => {
		if (a === null) {
			return 1
		}
		if (b === null) {
			return -1
		}
		return a.localeCompare(b)
	})
}

/**
 * One full rejection with the editor-selected set as the host saw it.
 *
 * Every photo here was removed, so the set carries no per-photo state and no
 * action: it is context for the follow-up conversation, nothing more.
 */
export const FullRejectionDetailPanel = ({
	detail
}: {
	detail: FullRejectionDetail
}) => {
	const { t } = useTranslation()

	return (
		<div className='flex flex-col gap-6'>
			<div className='grid grid-cols-2 gap-4 rounded-lg border border-neutral-200 bg-white p-4 lg:grid-cols-4'>
				<DetailField
					label={t('host_review:full_rejections.task')}
					value={
						detail.shoot.taskIdentifier ??
						String(detail.shoot.taskId)
					}
				/>
				<DetailField
					label={t('host_review:full_rejections.listing')}
					value={detail.shoot.listingTitle}
				/>
				<DetailField
					label={t('host_review:full_rejections.host')}
					value={detail.shoot.hostName}
				/>
				<DetailField
					label={t('host_review:full_rejections.photographer')}
					value={detail.shoot.photographerName}
				/>
				<DetailField
					label={t('host_review:full_rejections.closed_at')}
					value={formatDate(detail.closedAt, 'PPPp')}
				/>
				<DetailField
					label={t('host_review:full_rejections.removed_count')}
					value={String(detail.removedCount)}
				/>
			</div>

			<div className='flex flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-4'>
				<DetailField
					label={t('host_review:full_rejections.reason')}
					value={detail.rejectionReason}
				/>
				<div className='flex flex-col gap-0.5'>
					<span className='text-neutral-600 text-xs/4'>
						{t('host_review:full_rejections.note')}
					</span>
					{/* Verbatim: the note is the host's own words and the team reads
					    it to judge tone as much as content. */}
					<p className='whitespace-pre-wrap text-neutral-900 text-sm/5'>
						{detail.note ??
							t('host_review:full_rejections.no_note')}
					</p>
				</div>
			</div>

			<section className='flex flex-col gap-4'>
				<div className='flex flex-col gap-1'>
					<h2 className='font-bold text-neutral-900 text-sm/5'>
						{t('host_review:full_rejections.detail.photos_title', {
							count: detail.photos.length
						})}
					</h2>
					<p className='text-neutral-600 text-xs/4'>
						{t('host_review:full_rejections.detail.subtitle')}
					</p>
				</div>

				{groupByRoom(detail.photos).map(([roomKey, photos]) => (
					<div
						className='flex flex-col gap-2'
						key={roomKey ?? '__none'}
					>
						<h3 className='text-neutral-700 text-xs/4 capitalize'>
							{roomKey?.replaceAll('_', ' ') ??
								t(
									'host_review:full_rejections.detail.unallocated'
								)}
						</h3>
						<ul className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5'>
							{photos.map((photo) => {
								const thumbnail = getThumbnailUrl(
									photo.thumbnails
								)

								return (
									<li
										className='flex flex-col gap-1.5'
										key={photo.imageId}
									>
										<div className='aspect-3/2 overflow-hidden rounded-lg bg-neutral-100'>
											{thumbnail ? (
												<img
													alt={photo.name}
													className='size-full object-cover'
													height={200}
													loading='lazy'
													src={thumbnail}
													width={300}
												/>
											) : null}
										</div>
										<span
											className='truncate text-neutral-700 text-xs/4'
											title={photo.name}
										>
											{photo.name}
										</span>
									</li>
								)
							})}
						</ul>
					</div>
				))}
			</section>
		</div>
	)
}

FullRejectionDetailPanel.displayName = 'FullRejectionDetailPanel'
